import { Injectable, OnDestroy, effect } from '@angular/core';
import { AudioService } from './audio.service';
import { MapService } from '../api/services/map.service';
import { GraphMap } from '../api/interfaces/graph-node.interface';

@Injectable({
  providedIn: 'root'
})
export class MapAudioIntegrationService implements OnDestroy {
  private previousMap: GraphMap | undefined;

  constructor(
    private audioService: AudioService,
    private mapService: MapService
  ) {
    this.initializeMapAudioIntegration();
  }

  private initializeMapAudioIntegration(): void {
    // Use effect to watch signal changes and handle background sound playback
    effect(() => {
      const map = this.mapService.selectedMap();
      this.handleMapBackgroundSound(map);
    });
  }

  /**
   * Handle map style updates (called when map styles are updated)
   */
  handleMapStyleUpdate(updatedMap: GraphMap): void {
    const currentMap = this.mapService.selectedMap();
    if (currentMap && currentMap.id === updatedMap.id) {
      // Update background sound volume if it changed
      if (updatedMap.styles?.backgroundSoundVolume !== undefined) {
        this.audioService.updateMapSoundVolume(updatedMap.styles.backgroundSoundVolume);
      }
      
      // Handle sound URL changes
      if (updatedMap.styles?.backgroundSoundUrl !== undefined) {
        const currentSoundUrl = currentMap?.styles?.backgroundSoundUrl;
        const newSoundUrl = updatedMap.styles.backgroundSoundUrl;
        
        // If the sound URL changed, handle the transition
        if (currentSoundUrl !== newSoundUrl) {
          this.handleMapBackgroundSound(updatedMap);
        }
      }
    }
  }

  private handleMapBackgroundSound(map: GraphMap | undefined): void {
    const currentSoundUrl = map?.styles?.backgroundSoundUrl;
    const currentVolume = map?.styles?.backgroundSoundVolume ?? 0.5;
    
    const previousSoundUrl = this.previousMap?.styles?.backgroundSoundUrl;
    const previousVolume = this.previousMap?.styles?.backgroundSoundVolume ?? 0.5;
    
    // If no sound URL, stop any playing audio
    if (!currentSoundUrl) {
      this.audioService.stopMapBackgroundSound();
      this.previousMap = map;
      return;
    }
    
    // If URL changed, restart audio
    if (currentSoundUrl !== previousSoundUrl) {
      this.audioService.stopMapBackgroundSound();
      this.audioService.playMapBackgroundSound(currentSoundUrl, currentVolume);
    }
    // If only volume changed, just update volume without restarting
    else if (currentVolume !== previousVolume) {
      this.audioService.updateMapSoundVolume(currentVolume);
    }
    // If no audio is playing but we have a URL, start it
    else if (!this.audioService.isMapAudioPlaying() && currentSoundUrl) {
      this.audioService.playMapBackgroundSound(currentSoundUrl, currentVolume);
    }
    
    this.previousMap = map;
  }

  ngOnDestroy(): void {
    // Effect cleanup is handled automatically by Angular
  }
}